import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RotateCcw, Trash2, AlertTriangle, ShieldAlert } from "lucide-react";
import { adminListTrash, adminRestoreListing, adminDeleteListing } from "../../lib/adminApi";

/**
 * Trash view — the ONLY place where permanent deletion is possible.
 * Restore returns the listing to its previous status (draft/published/archived).
 * Permanent delete requires an explicit second confirmation and cannot be undone.
 */
export default function TrashView() {
  const [rows, setRows] = useState([]);
  const [confirmId, setConfirmId] = useState(null); // id currently in the "type slug" confirm dialog
  const [typed, setTyped] = useState("");

  const load = async () => {
    try { setRows(await adminListTrash()); }
    catch { toast.error("Could not load Trash"); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (r) => {
    try { await adminRestoreListing(r.id); toast.success("Restored"); load(); }
    catch { toast.error("Restore failed"); }
  };
  const permDelete = async (r) => {
    if (typed !== r.slug) { toast.error("Type the exact slug to confirm."); return; }
    try {
      await adminDeleteListing(r.id);
      toast.success("Permanently deleted");
      setConfirmId(null); setTyped("");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10" data-testid="publisher-trash">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="inline-flex items-center gap-2 font-mono-wr text-[10px] uppercase tracking-widest hover:text-[var(--wr-red)]" data-testid="trash-back-dashboard">
            <ArrowLeft size={14}/> Dashboard
          </Link>
          <div>
            <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)]">/ PUBLISHER</p>
            <h1 className="font-editorial text-4xl mt-1">Trash</h1>
            <p className="mt-2 text-[13px] text-[var(--wr-ink-2)] max-w-xl">
              Trashed listings are hidden from the public site, sitemap and search. Restore any time — or delete them permanently below.
            </p>
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="border border-[var(--wr-border)] p-16 text-center" data-testid="trash-empty">
          <p className="font-editorial text-2xl mb-2">Nothing in Trash.</p>
          <p className="text-[13px] text-[var(--wr-ink-2)]">Deleted listings will appear here.</p>
        </div>
      ) : (
        <ul className="divide-y divide-[var(--wr-border)] border border-[var(--wr-border)]" data-testid="trash-list">
          {rows.map(r => (
            <li key={r.id} className="p-5" data-testid={`trash-row-${r.slug}`}>
              <div className="flex flex-wrap items-start gap-4">
                <div className="flex-1 min-w-[240px]">
                  <p className="font-editorial text-xl leading-tight">{r.name_en || <span className="text-[var(--wr-ink-2)]">Untitled</span>}</p>
                  <p className="text-[var(--wr-ink-2)] text-[13px]">{r.name_ro}</p>
                  <p className="mt-1 font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">
                    {r.listing_type || "—"} · {r.region_slug || "—"} · trashed {(r.trashed_at || "").slice(0,10)} · was {r.status}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={()=>restore(r)} className="inline-flex items-center gap-1.5 px-4 py-2 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px] uppercase tracking-widest" data-testid={`trash-restore-${r.slug}`}>
                    <RotateCcw size={12}/> Restore
                  </button>
                  <button onClick={()=>{ setConfirmId(r.id); setTyped(""); }} className="inline-flex items-center gap-1.5 px-4 py-2 border border-[var(--wr-red)] text-[var(--wr-red)] hover:bg-[var(--wr-red)] hover:text-white font-mono-wr text-[10px] uppercase tracking-widest" data-testid={`trash-permdelete-${r.slug}`}>
                    <Trash2 size={12}/> Delete permanently
                  </button>
                </div>
              </div>
              {confirmId === r.id && (
                <div className="mt-4 border border-[var(--wr-red)] bg-[var(--wr-surface-2)] p-4" data-testid={`trash-confirm-${r.slug}`}>
                  <div className="flex items-start gap-3">
                    <ShieldAlert size={18} className="text-[var(--wr-red)] mt-1 shrink-0" />
                    <div className="flex-1">
                      <p className="font-editorial text-lg leading-tight">Permanent deletion cannot be undone.</p>
                      <p className="mt-1 text-[13px] text-[var(--wr-ink-2)]">
                        This removes the listing document and its audit trail relationships. Uploaded images remain in storage unless you separately delete them. Type the slug <code className="font-mono-wr bg-white px-1.5 py-0.5 border border-[var(--wr-border)]">{r.slug}</code> below to confirm.
                      </p>
                      <input value={typed} onChange={(e)=>setTyped(e.target.value)} placeholder={r.slug}
                        className="mt-3 w-full max-w-md border border-[var(--wr-border)] bg-white px-3 py-2 font-mono-wr focus:outline-none focus:border-[var(--wr-red)]"
                        data-testid={`trash-confirm-input-${r.slug}`} autoFocus />
                      <div className="mt-3 flex gap-2">
                        <button onClick={()=>permDelete(r)} disabled={typed !== r.slug} className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--wr-red)] text-white hover:bg-[var(--wr-ink)] disabled:opacity-40 font-mono-wr text-[10px] uppercase tracking-widest" data-testid={`trash-confirm-submit-${r.slug}`}>
                          <AlertTriangle size={12}/> I understand — delete forever
                        </button>
                        <button onClick={()=>{ setConfirmId(null); setTyped(""); }} className="px-4 py-2 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px] uppercase tracking-widest" data-testid={`trash-confirm-cancel-${r.slug}`}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
